import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Statistic, Card, Row, Col, Select, Space, Button, Table, Empty, Tag } from 'antd'
import {
  CarOutlined,
  MessageOutlined,
  DollarOutlined,
  TeamOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import PageLoader from 'components/Layouts/PageLoader'
import { getRequest, dutyAnalyticsUrl } from '../config'
import SegmentBreakdown from './Analytics/SegmentBreakdown'
import WeekdayActivity from './Analytics/WeekdayActivity'
import ServiceBreakdown from './Analytics/ServiceBreakdown'
import { PERIOD_OPTIONS, formatMoney } from './Analytics/utils'
import IdentityLabel from '../components/IdentityLabel'

const EMPTY_SECTION = { total: 0, segments: [] }

// Find one segment's count/percentage by key (for headline highlight cards).
const pick = (section, key) =>
  (section.segments || []).find(s => s.key === key) || { count: 0, percentage: 0 }

const DashboardOverview = props => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [period, setPeriod] = useState('all')

  const getAnalytics = async (selectedPeriod = period) => {
    const response = await getRequest(
      `${dutyAnalyticsUrl}?period=${selectedPeriod}`,
      'analytics',
      setLoading
    )
    if (response.success) {
      setData(response.data)
    }
  }

  useEffect(() => {
    getAnalytics(period)
    // eslint-disable-next-line
  }, [period])

  const cars = (data && data.cars) || EMPTY_SECTION
  const conversations = (data && data.conversations) || EMPTY_SECTION
  const dutyDocuments = (data && data.dutyDocuments) || EMPTY_SECTION
  const transactions = (data && data.transactions) || EMPTY_SECTION
  const customers = (data && data.customers) || { total: 0, dealers: 0, top: [] }

  const availableCars = pick(cars, 'AVAILABLE')
  const soldCars = pick(cars, 'SOLD')
  const completedConversations = pick(conversations, 'completed')
  const cancelledConversations = pick(conversations, 'cancelled')
  const successfulPayments = pick(transactions, 'SUCCESSFUL')

  const goCars = () => props.history.push('/cars')
  const goConversations = () =>
    props.history.push('/duty-assistant/conversations')
  const goTransactions = () => props.history.push('/transactions')
  const goCustomers = () => props.history.push('/custom-duties/customers')

  return (
    <PageLoader className="container" loading={loading}>
      <div style={{ padding: '8px 4px 24px' }}>
        {/* Header + period filter */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            marginBottom: 20
          }}
        >
          <h2 style={{ margin: 0 }}>Dashboard Overview</h2>
          <Space>
            <span style={{ color: '#888' }}>Period:</span>
            <Select
              value={period}
              style={{ width: 180 }}
              onChange={setPeriod}
              options={PERIOD_OPTIONS}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => getAnalytics(period)}
            >
              Refresh
            </Button>
          </Space>
        </div>

        {/* Headline highlight cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8} xl={6}>
            <Card style={{ cursor: 'pointer' }} onClick={goCars}>
              <Statistic
                title="Cars in Inventory"
                value={cars.total}
                prefix={<CarOutlined />}
              />
              <div style={{ color: '#888', marginTop: 6 }}>
                {availableCars.count} available ({availableCars.percentage}%) ·{' '}
                {soldCars.count} sold ({soldCars.percentage}%)
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <Card style={{ cursor: 'pointer' }} onClick={goConversations}>
              <Statistic
                title="Duty Conversations"
                value={conversations.total}
                prefix={<MessageOutlined />}
              />
              <div style={{ color: '#888', marginTop: 6 }}>
                {completedConversations.count} completed ·{' '}
                {cancelledConversations.count} cancelled
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <Card style={{ cursor: 'pointer' }} onClick={goTransactions}>
              <Statistic
                title="Duty Revenue (paid)"
                value={formatMoney(
                  transactions.totalRevenue,
                  transactions.currency
                )}
                prefix={<DollarOutlined />}
              />
              <div style={{ color: '#888', marginTop: 6 }}>
                {successfulPayments.count} successful of {transactions.total}{' '}
                payments
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <Card style={{ cursor: 'pointer' }} onClick={goCustomers}>
              <Statistic
                title="Customers"
                value={customers.total}
                prefix={<TeamOutlined />}
              />
              <div style={{ color: '#888', marginTop: 6 }}>
                {customers.dealers} registered dealer
                {customers.dealers === 1 ? '' : 's'}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Each service the assistant sells, one tab each, so history and tracking have their
            numbers without a section of their own each (owner, 2026-10-29). */}
        <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
          <Col xs={24}>
            <ServiceBreakdown
              services={data && data.services}
              onOpenConversations={path => props.history.push(path)}
            />
          </Col>
        </Row>

        {/* Busiest days of the week (completed conversations + payments) */}
        <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
          <Col xs={24}>
            <WeekdayActivity data={data && data.activityByWeekday} />
          </Col>
        </Row>

        {/* Detailed breakdowns */}
        <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
          <Col xs={24} lg={12}>
            <SegmentBreakdown
              title="Inventory by Status"
              total={cars.total}
              segments={cars.segments}
            />
          </Col>
          <Col xs={24} lg={12}>
            <SegmentBreakdown
              title="Duty Documents by Status"
              total={dutyDocuments.total}
              segments={dutyDocuments.segments}
            />
          </Col>
          <Col xs={24} lg={12}>
            <SegmentBreakdown
              title="Duty Payments by Status"
              total={transactions.total}
              segments={transactions.segments}
              extra={
                <span style={{ color: '#3f8600', fontWeight: 600 }}>
                  {formatMoney(transactions.totalRevenue, transactions.currency)}
                </span>
              }
            />
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title="Top customers by duty requests"
              extra={
                <Button type="link" style={{ padding: 0 }} onClick={goCustomers}>
                  View all
                </Button>
              }
            >
              <Table
                size="small"
                rowKey={(r, i) => i}
                dataSource={customers.top || []}
                pagination={false}
                locale={{ emptyText: <Empty description="No customer activity yet" /> }}
                columns={[
                  {
                    title: 'Customer',
                    key: 'phoneMasked',
                    render: (_, r) => <IdentityLabel value={r} />
                  },
                  {
                    title: 'Requests',
                    dataIndex: 'requestCount',
                    key: 'requestCount',
                    width: 100
                  },
                  {
                    title: 'Paid',
                    key: 'paid',
                    width: 90,
                    render: (_, r) => (
                      <Tag color={r.paidCount ? 'green' : 'default'}>
                        {r.paidCount} paid
                      </Tag>
                    )
                  }
                ]}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </PageLoader>
  )
}

DashboardOverview.propTypes = {
  history: PropTypes.object
}

export default DashboardOverview
